import Phaser from 'phaser';
import {
  SCENE, GAME_WIDTH, STREET_WORLD_WIDTH, STREET_DOORS, STREET_SPAWNS,
  STREET_SPAWN_Y, STREET_FEET, STREET_FEET_Y, STREET_RIDE, STREET_WALK_CENTER_Y } from '../config.js';
import { depthSortScene } from '../ui/depthSort.js';
import { currentPhase } from '../state/dayCycle.js';
import { doorClosed, CLOSED_TOAST } from '../street/streetLooks.mjs';
import { createStreetLook } from '../street/streetLook.js';
import { attachStreetSky } from '../street/StreetSky.js';

import { MoneyBar } from '../ui/MoneyBar.js';
import { WalkController } from '../ui/WalkController.js';
import { NatalkaSprite } from '../ui/NatalkaSprite.js';
import { BikeController } from '../ui/BikeController.js';
import { CarController } from '../ui/CarController.js';
import { lookahead } from '../ui/cameraLookahead.mjs';
import { reportLocation } from '../ui/locationTracker.js';
import { gameState } from '../state/gameState.js';
import { messageFor } from '../api/errors.js';
import { showToast } from '../ui/Toast.js';
import { ChatBubble } from '../ui/ChatBubble.js';
import { abortToast, isSchoolCarId } from '../ui/schoolCar.mjs';
import { summaryBody, summaryTitle } from '../ui/practicalSummary.mjs';
import { celebrate } from '../ui/celebration.js';
import { showLicenceModal } from '../ui/LicenceModal.js';
import { showScrollableTextModal } from '../ui/QuizModal.js';
import { attachCoordsDebug } from '../ui/CoordsDebug.js';
import { attachFriendsButton } from '../ui/FriendsButton.js';
import { attachRemotePresence } from '../ui/remotePresence.js';
import { attachVehiclePresence, renderParkedCar } from '../ui/vehiclePresence.js';
import { hostParkedBike } from '../ui/hostParkedBike.js';
import { iconText } from '../ui/iconText.js';
import { pxPerSec } from '../ui/carPhysics.mjs';
import {
  bikeWheelOffset, CHARACTER_ART_H, CHARACTER_HALF_H, OUTDOOR_CHARACTER_SCALE,
  STREET_LIMIT_KMH, centreYFromGround, groundYFromCentre, parkedBikeOutdoor, perspectiveScale, PERSPECTIVE,
} from '../ui/carCatalog.mjs';
import { boardableCar, exitPosition } from '../ui/passengerLogic.mjs';
import { isInputLocked } from '../ui/inputLock.js';
import { virtualInput } from '../ui/virtualInput.js';
import { attachRoomChat } from '../ui/ChatInput.js';
import { realtime } from '../api/realtime.js';
import { queueMissing, loadCtx, prioritiseFor } from '../ui/sceneLoading.js';
import { needsFor } from '../assetManifest.mjs';
import { preloader } from './PreloadScene.js';

// One baked street image (bg_street.webp, 8358×941 — see streetSpawns.mjs) scaled to
// fill the scene height; the world is that wide.
const WORLD_WIDTH = STREET_WORLD_WIDTH;  // 6395

// Street camera feel: the camera always ends up with the player centred, but gets there
// with a soft lag instead of tracking every step. Done by hand in update() (not
// cameras.main.startFollow) so the easing is frame-rate independent and the scroll stays
// fractional — Phaser's follow with roundPixels moves in whole-pixel steps, which at a
// small lerp reads as stutter. CAMERA_TAU is the time constant (s): ~63% of the gap is
// closed after TAU, ~95% after 3·TAU. At bike speed (420 px/s) the steady lag ≈ v·TAU ≈ 150 px.
const CAMERA_TAU = 0.15;   // was 0.35: with an animated walk the ~1 s scroll ramp read as "walking slowly" at every start
// …and tightens up as speed rises: at car speeds a 0.35 s lag leaves the car pinned to the
// screen edge, so the driving controller feeds `_camTau` down to CAMERA_TAU_FAST.
const CAMERA_TAU_FAST = 0.15;
const CAMERA_TAU_FAST_AT = 2000;   // px/s at which the lag has fully tightened

// Door → target scene. Doors with `comingSoon` show a toast instead of entering.
// Positions (world x) come from STREET_DOORS in streetSpawns.mjs.
const DOOR_TARGETS = {
  CAR_DEALER:     { target: SCENE.CAR_DEALER },
  DRIVING_SCHOOL: { target: SCENE.DRIVING_SCHOOL },
  GAME_HALL:      { target: SCENE.GAME_HALL },
  BIKE_SHOP:      { target: SCENE.BIKE_SHOP },
  FURNITURE:      { target: SCENE.FURNITURE_SHOP },
  CLOTHING:       { target: SCENE.CLOTHING_SHOP },
  PET:            { target: SCENE.PET_SHOP },
  SHOP:           { target: SCENE.SHOP },
};
const SHOPS = STREET_DOORS.map(d => ({ ...d, ...DOOR_TARGETS[d.key] }));

// Riding along in someone else's car. The passenger steers nothing — the seat is just a
// promise that wherever the driver's car is, that's where you are — so the frame that
// would have run the walker copies the car's position instead, and broadcasts it at the
// walker's own cadence so the room's roster doesn't leave you standing on the pavement
// you boarded from.
const RIDE_BROADCAST_MS = 100;             // 10 Hz, same as the walker
const LABEL_RIDE_EXIT = 'Wysiądź {walk}';
const rideBoardLabel = (name) => `Wsiądź do auta ${name} {car}`;

// The practical EXAM: the street is only the drive-out. The school's Yaris waits in front
// of the school, the instructor says which way, and the LEFT edge — which normally just
// stops you — is the way onto the training route (TrainingRouteScene, its own scene).
// A practical LESSON no longer comes through here at all (Task 5): the instructor's menu
// starts it straight into TrainingRouteScene from her desk, so `_lesson` below is only
// ever populated for `kind === 'exam'`.
const LESSON_EDGE_X = 80;
const LESSON_BUBBLE_MS = 3000;
const LESSON_BUBBLE_PL = 'Jedziemy w lewo, na trasę!';

// Pull one-shot start data off Phaser's persistent settings object: returns the values and
// removes the keys, so the next `scene.start(OUTSIDE)` that passes nothing gets nothing.
function takeOnce(data, keys) {
  const out = {};
  if (!data) return out;
  for (const k of keys) {
    out[k] = data[k];
    delete data[k];
  }
  return out;
}

// You go into a shop by walking UP the pavement to its door: the trigger is the child's
// FEET being inside a box at the storefront, so it fires wherever she is standing when she
// reaches the back of the pavement and never while she is strolling along the kerb.
// `DOOR_W` is generous (the old legs-box was 80 px wide on top of a 90 px door) so a door
// is easy to hit with a keyboard.
const DOOR_W = 170;
const DOOR_FEET = {
  top: STREET_FEET.backY - 30,                 // a little slack above the thresholds
  bottom: STREET_FEET.backY + 10,              // …and the doorway ends well short of the kerb
};

// The street is drawn at ONE real-world scale: 100 px to the metre, so the 1.2 m child is
// 120 px tall (half her 240 px art) beside a 154 px Fiat Panda. Sprites scale about their
// centre, so every y that used to mean "a 240 px character's centre" moves DOWN by half the
// height she lost — the lines below are written as the FEET line they have always been.
// …and with a y-perspective on top of that (carCatalog's PERSPECTIVE): the pavement's
// walking line is the reference plane, and everything standing further DOWN the screen — a
// car in the near lane, a bike out in the road — is drawn proportionally bigger. So every
// size and every centre↔ground conversion below goes through that pair of functions rather
// than through a constant half-height.
const WORLD_SCALE = OUTDOOR_CHARACTER_SCALE;
const CHAR_HALF_H = CHARACTER_HALF_H;      // 60, on the reference plane
// The pavement she walks on (see streetSpawns.mjs for how the band was measured off the
// art): feet between the shop thresholds and the kerb, never on the road below it.
const FEET_FLOOR_Y = STREET_FEET.backY;                  // feet at the storefronts
const FEET_KERB_Y = STREET_FEET.frontY;                  // …and at the grass verge
const FLOOR_Y = STREET_WALK_CENTER_Y.min;                // sprite-centre y at the storefronts
const WALK_Y_MAX = STREET_WALK_CENTER_Y.max;             // …and at the kerb
const DEFAULT_SPAWN_Y = STREET_SPAWN_Y;   // mid-pavement: clear of the doors, so a spawn
                                          // doesn't re-trigger entry
// A restored/handed-in y is clamped into the walking band — but never above the default
// line: the top of the band lies inside the shop-door zone, and a reload that put her there
// walked her straight into the nearest shop.
const clampY = (y) => Math.min(WALK_Y_MAX, Math.max(DEFAULT_SPAWN_Y, Number.isFinite(y) ? y : DEFAULT_SPAWN_Y));

export class OutsideScene extends Phaser.Scene {
  constructor() { super(SCENE.OUTSIDE); }

  preload() {
    // Staged loading: only what this scene draws now, only if the cache lacks it.
    queueMissing(this, needsFor(this.scene.key, loadCtx()));
    prioritiseFor(this.scene.key);
  }

  create(data) {
    // Read by everything that draws a person or a vehicle here (NatalkaSprite, RemoteAvatar,
    // NpcAvatar, BikeController) — set before any of them exists.
    this.worldScale = WORLD_SCALE;

    // `data` IS Phaser's live `sys.settings.data`, and a `scene.start(OUTSIDE)` with no data
    // of its own (VisitSceneBase's "leave the visit" exits, for instance) leaves the PREVIOUS
    // start's object in place. So everything one-shot is read once and then deleted from it:
    // otherwise a later plain return to the street would re-spawn you in front of the school,
    // re-open the last lesson's summary, or put you back in a car you got out of long ago.
    const arrival = takeOnce(data,
      ['spawnX', 'spawnY', 'lesson', 'drivingCarId', 'practicalResult', 'toast', 'upExit']);

    this._spawnX = arrival.spawnX ?? (WORLD_WIDTH - 200);
    // Saved positions from before the pavement moved (BootScene resumes one) put her on the
    // grass below the road — the walking band is the truth, whoever asked.
    this._spawnY = clampY(arrival.spawnY ?? DEFAULT_SPAWN_Y);
    // Driving-school lesson or exam in progress — `{ route, kind, lesson? }`: the school car
    // waits in front of the school and the left edge hands over to the route. Scene
    // instances are reused, so a visit WITHOUT one has to forget the previous visit's.
    this._lesson = arrival.lesson?.kind ? { ...arrival.lesson } : null;
    this._lessonBubble = null;   // scene instances are reused — don't inherit a dead reference
    this._aborting = false;
    // No camera bounds: Phaser clamps scroll as if the zoom were centred (offset
    // (width - displayWidth) / 2), which breaks the top-left retina zoom — the street
    // clamps in world units in _desiredScrollX() instead.
    this._camTarget = null;   // scene instances are reused: forget the previous visit's follow target
    this._camTau = CAMERA_TAU;

    // The street picture — stores are part of the artwork, scaled to fill the scene height with the world as
    // wide as the scaled image — in the look of the current phase (morning/day/evening/night), crossfading
    // when the phase changes (street/streetLook.js). It comes as four tiles (a single 8358-px texture is over
    // phone GPUs' limit and drew black — see streetSpawns.STREET_TILES), with the flower-bed foreground copied
    // from them. Its sky is cut out: the farm's sky, sun, moon and clouds are drawn behind it (StreetSky.js).
    this._streetSky = attachStreetSky(this);
    this._streetLook = createStreetLook(this, { phase: currentPhase() });
    // the mask graphics are not on the display list
    this.events.once('shutdown', () => { this._streetLook?.destroy(); this._streetLook = null; });

    // Door zones — derived from the door centres baked into the image. The rectangle is in
    // FEET coordinates: (mover x, mover feet y) inside it means "standing in the doorway".
    this.doorZones = [];
    for (const shop of SHOPS) {
      const door = new Phaser.Geom.Rectangle(
        shop.x - DOOR_W / 2, DOOR_FEET.top, DOOR_W, DOOR_FEET.bottom - DOOR_FEET.top);
      this.doorZones.push({ key: shop.key, rect: door, target: shop.target ?? null, comingSoon: shop.comingSoon ?? null });
    }

    // Spawn position — z data?.spawnX (sklep z którego wracamy) lub default (powrót z domu).
    // Y na chodniku, przy krawężniku (DEFAULT_SPAWN_Y), żeby spawn nie wchodził od razu do sklepu.
    this.natalka = new NatalkaSprite(this, this._spawnX, this._spawnY, { worldScale: WORLD_SCALE });
    this.natalka.setOutfit(gameState.raw?.inventory?.equipped);
    this.natalka.setDepth(10);  // y-sorted every frame (see update); parked bikes stay at depth 1
    reportLocation(this, this.natalka.x, this.natalka.y, { immediate: true });
    // FLOOR_Y (sprite centre) → her feet at the shop thresholds, the top of the pavement.
    this.walker = new WalkController(this, this.natalka, {
      floorY: FLOOR_Y,
      bottomY: WALK_Y_MAX,
      spriteHeight: CHARACTER_ART_H * WORLD_SCALE,
      onBroadcast: (p) => realtime.move(p),
    });
    // Cooldown: blokuje trigger drzwi przez 600ms po spawn (żeby spawn nie wszedł od razu do sklepu)
    this._spawnGuardUntil = Date.now() + 600;
    // Walked out of a store upward: up stays ignored until she walks another way (ui/upBlock.mjs).
    this.walker.blockUp = !!arrival.upExit;
    // Override world bounds for walker
    if (this.walker.boundsXMax !== undefined) {
      this.walker.boundsXMax = WORLD_WIDTH - 60;
      this.walker.boundsXMin = 60;
    }

    this._followWithLag(this.natalka);

    // Bike — rideable here too; camera follows whichever sprite is active.
    const rig = this.cache.json.get('bike_rig');
    const wheel = (kind, type) => bikeWheelOffset(kind, type, rig);
    this.bike = new BikeController(this, {
      natalka: this.natalka,
      // The rider is a different height from the walker, and it is her WHEELS that have to
      // keep to the same pavement her feet do. Both bikes' riders are one height (the layered
      // rider, bike plan 3), so the pink one's offset serves both.
      // A bike is traffic: it rides the pavement AND the road, from the shopfronts down to
      // the grass verge (STREET_RIDE), while the walker stays on the pavement. The bounds
      // are on the rider's CENTRE, so they are the ground lines converted through the
      // perspective — the rider grows on the way down, and its centre moves further.
      floor: {
        floorY: centreYFromGround(wheel('rider', 'pink'), STREET_RIDE.backY),
        bottomY: centreYFromGround(wheel('rider', 'pink'), STREET_RIDE.frontY),
      },
      parkedDefaults: {
        pink: { x: this._spawnX, y: centreYFromGround(wheel('parked', 'pink'), STREET_FEET_Y) },
        blue: { x: this._spawnX + 130, y: centreYFromGround(wheel('parked', 'blue'), STREET_FEET_Y) },
      },
      // Getting on puts the wheels where her feet were; getting off clamps her feet between the
      // walking band's floor and kerb lines (the bike stays in the road if that is where she left it).
      mountY: () => this._feetY(),
      dismountY: (groundY) => centreYFromGround(CHAR_HALF_H,
        Math.min(FEET_KERB_Y, Math.max(FEET_FLOOR_Y, groundY))),
      onMount: (rider) => this._followWithLag(rider),
      onDismount: (natalka) => this._followWithLag(natalka),
      onBroadcast: (p) => realtime.move(p),
      canRide: () => !this.carCtl?.isDriving && !this._ridingIn,
    });
    this.bike.riderWalker.boundsXMin = 60;
    this.bike.riderWalker.boundsXMax = WORLD_WIDTH - 60;

    // Cars — your own ones parked at the kerb plus the one you're driving. Built after the
    // bike so its SPACE handler runs second and its mobile action wins the tie.
    this.carCtl = new CarController(this, {
      natalka: this.natalka,
      presence: null,                  // set below, once remote presence is attached
      worldWidth: WORLD_WIDTH,
      onEnter: (sprite) => this._followWithLag(sprite),
      onExit: (natalka) => this._followWithLag(natalka),
      onBroadcast: (p) => realtime.move(p),
      onKick: () => realtime.carKick(),
      onAbortLesson: () => this._abortLesson(),
      canDrive: () => !this._ridingIn,
      // One limit for the whole town — the HUD's sign says so from the moment you set off.
      speedLimit: STREET_LIMIT_KMH,
      // Getting out of a car puts her back on the pavement, not in the lane she parked in.
      walkY: () => DEFAULT_SPAWN_Y,
    });
    this._carBlockToastAt = 0;

    // Passenger state — set up before anything can consult it (see the presence hook).
    this._carPassengers = new Map();   // car_id -> [player id], the server's word for it
    this._riders = new Set();          // …flattened: every peer currently in a seat
    this._ridingIn = null;             // the car WE are riding in, or null
    this._rideAt = null;               // its last seen {x, y, facing} — where we get out
    this._rideBroadcastAt = 0;
    this._rideActionLabel = null;      // what we last put on the shared action button
    this._leftCarId = null;            // the car we most recently got out of…
    this._leftAt = 0;                  // …and when, so a late `vehicle_parked` can adjust

    this._exitTriggered = false;
    this._bikeBlockToastAt = 0;
    this._comingSoonToastAt = 0;   // scene instances are reused — don't inherit the last visit's throttle
    this._closedToastAt = 0;

    new MoneyBar(this);

    attachFriendsButton(this);

    // MoneyBar must be camera-pinned (not in world). Quick fix: scrollFactor 0
    // Actually MoneyBar uses scene.add directly which is in world. We need to fix this:
    // Set scrollFactor for MoneyBar's text+rectangle to 0 so it stays on screen.
    // BUT MoneyBar's children are added internally. Workaround: we'll accept money bar scrolls with camera here.

    this._unsub = gameState.on(() => {
      this.natalka.setOutfit(gameState.raw?.inventory?.equipped);
      this.bike.refresh();
      this.carCtl.onState();
    });
    this.events.on('shutdown', () => this._unsub?.());

    attachCoordsDebug(this);

    // Who is riding in whose car has to exist BEFORE the avatars do: `isHidden` is
    // consulted the moment an avatar is spawned, so a rider whose avatar arrives after the
    // `car_passengers` that seated them is hidden on creation instead of jogging along
    // beside the car until the next broadcast.
    this._presence = attachRemotePresence(this, 'town', {
      isHidden: (peerId) => this._riders.has(peerId),
    });
    this.carCtl.presence = this._presence;
    this._attachVehicles();
    this._attachPassengers();
    attachRoomChat(this, 'town');

    // Arrived already behind the wheel (out of the garage / off a dealer's forecourt).
    if (arrival.drivingCarId) this.carCtl.startFrom(arrival.drivingCarId);
    // …or in the school's Yaris, with the instructor beside you and a route to reach.
    if (this._lesson) this._startLesson();

    // Back from a training route: something to say (a refused finish), a result to show,
    // or both. The toast belongs here rather than on the route scene we came from — that
    // one is torn down the instant it hands over, taking its toast with it.
    if (arrival.toast) showToast(this, arrival.toast);
    if (arrival.practicalResult) this._showPracticalResult(arrival.practicalResult);
  }

  // The end of a practical lesson or the exam, shown once we're back in front of the school:
  // the title says how it went, the body is one row per task the route asked for — icon,
  // what it was, and how it went — with the exam's fault count underneath. The counts and
  // the verdict are the SERVER's (see practicalSummary.mjs / driving.score_tasks).
  async _showPracticalResult(res) {
    // A passed exam is the moment of the whole driving school: confetti, then the new
    // licence card itself, and only then the task-by-task summary.
    if (res.kind === 'exam' && res.passed) {
      await celebrate(this, { title: 'Egzamin zdany! {party}', subtitle: 'Masz prawo jazdy!' }).catch(() => {});
      await showLicenceModal(this).catch(() => {});
    }
    showScrollableTextModal(this, { title: summaryTitle(res), body: summaryBody(res) })
      .catch(() => {});
  }

  // The school car appears in front of the school with us already in it (the lesson was
  // paid for/started at the instructor's desk — see DrivingSchoolMenu), and the instructor
  // says the one thing the child needs to know: drive LEFT.
  _startLesson() {
    this.carCtl.startSchoolCar({
      x: STREET_SPAWNS.DRIVING_SCHOOL, facing: 'left', lesson: this._lesson,
    });
    if (!this.carCtl.isDriving) return;
    this._lessonBubble = new ChatBubble(this, this.carCtl.sprite, LESSON_BUBBLE_PL);
    this.time.delayedCall(LESSON_BUBBLE_MS, () => {
      this._lessonBubble?.destroy();
      this._lessonBubble = null;
    });
  }

  // „Przerwij lekcję/egzamin" from the car's menu. The controller only asked the question —
  // cancelling the session on the server, saying so, and putting the child back on the
  // pavement in front of the school is ours. The Yaris is never left standing at the kerb.
  //
  // Input is locked and `_lesson` cleared BEFORE the (async) abort request goes out: while
  // it's in flight the child must not be able to drive across the left edge and trigger the
  // TRAINING_ROUTE hand-off on a lesson that's already being torn down. `kind` is kept in a
  // local since `_lesson` is gone by the time the request resolves.
  async _abortLesson() {
    if (!this._lesson || this._aborting) return;
    this._aborting = true;
    this.carCtl.lockInput();
    const kind = this._lesson.kind === 'exam' ? 'exam' : 'lesson';
    this._lesson = null;
    try {
      if (kind === 'exam') await gameState.drivingPracticalAbort();
      else await gameState.drivingLessonAbort();
      showToast(this, abortToast(kind));
    } catch (e) {
      // The session is gone one way or another (expired, aborted in another tab): still get
      // out of the car — leaving the child sitting in a Yaris with no lesson is worse.
      showToast(this, messageFor(e));
    } finally {
      this._aborting = false;
      this._endLesson();
    }
  }

  // Out of the school car and back on the pavement in front of the school. `leaveSchoolCar`
  // removes the Yaris (it never parks) and broadcasts a walk, so peers drop the car too;
  // the walk is re-sent from the pavement because that is where we actually end up.
  _endLesson() {
    this._lesson = null;
    this._lessonBubble?.destroy();
    this._lessonBubble = null;
    if (!this.carCtl.isSchoolCar) return;
    this.carCtl.leaveSchoolCar();
    const x = STREET_SPAWNS.DRIVING_SCHOOL, y = DEFAULT_SPAWN_Y;
    this.natalka.setPosition(x, y);
    realtime.move({ x, y, facing: 'right', mode: 'walk' });
    reportLocation(this, x, y, { immediate: true });
    this._spawnGuardUntil = Date.now() + 600;   // …and don't walk straight back inside
  }

  // Other players' cars: the ones standing at the kerb (drawn here) and the ones being
  // driven past (drawn by their driver's RemoteAvatar, see remotePresence). YOUR OWN cars
  // belong to the car controller — it has to know where they are to let you get in.
  _attachVehicles() {
    this._parkedCars = new Map();     // car_id -> Container
    this._peerBikes = new Map();      // bike_id -> BikeSprite, other players' bikes parked on the street

    const drop = (carId) => {
      this._parkedCars.get(carId)?.destroy();
      this._parkedCars.delete(carId);
    };
    const draw = (car) => {
      if (!car?.car_id) return;
      drop(car.car_id);   // a re-park of a car we already show: move it, don't double it
      if (car.owner_id && car.owner_id === window.__myId) return;
      this._parkedCars.set(car.car_id, renderParkedCar(this, car));
      // renderParkedCar draws a grey box when car_<model>_side hasn't landed yet — redraw
      // this one car once it has.
      const key = `car_${car.model}_side`;
      if (!this.textures.exists(key)) preloader.whenReady(key, () => { if (this._parkedCars.has(car.car_id)) draw(car); });
    };

    // Other players' bikes standing on the street (YOUR OWN are the bike controller's), drawn like a
    // visited host's: read-only, hover for whose it is.
    const rig = this.cache.json.get('bike_rig');
    const bikeTip = iconText(this, 0, 0, '', {
      fontFamily: 'system-ui', fontSize: '16px', color: '#fff',
      backgroundColor: '#000000aa', padding: { x: 8, y: 4 },
    }).setOrigin(0.5, 1).setDepth(50).setVisible(false);
    const dropBike = (bikeId) => {
      this._peerBikes.get(bikeId)?.destroy();
      this._peerBikes.delete(bikeId);
      bikeTip.setVisible(false);
    };
    const drawBike = (b) => {
      if (!b?.bike_id || (b.type !== 'pink' && b.type !== 'blue')) return;
      dropBike(b.bike_id);   // moved along the street: redraw, don't double it
      if (b.owner_id && b.owner_id === window.__myId) return;
      const defaultY = centreYFromGround(bikeWheelOffset('parked', b.type, rig), STREET_FEET_Y);
      const { groundY } = parkedBikeOutdoor(b.type, { storedY: b.y, defaultY }, PERSPECTIVE, rig);
      const sprite = hostParkedBike(this, b.type,
        { parked_x: b.x, parked_y: b.y, parked_facing: b.facing, design: b.design },
        { x: b.x, defaultY, profile: PERSPECTIVE, tip: bikeTip }).setDepth(groundY);
      sprite.on('pointerover', () => bikeTip.setText(`Rowerek: ${b.owner_name ?? ''} {bike}`));
      this._peerBikes.set(b.bike_id, sprite);
    };

    this._detachVehicles = attachVehiclePresence(this, 'town', {
      onRoster: (list) => {
        for (const id of [...this._parkedCars.keys()]) drop(id);
        list.forEach(draw);
      },
      onBikeRoster: (list) => {
        for (const id of [...this._peerBikes.keys()]) dropBike(id);
        list.forEach(drawBike);
      },
      onBikeParked: drawBike,
      onBikeRemoved: dropBike,
      onParked: (car) => { draw(car); this._settleAfterRide(car); },
      onRemoved: (carId) => { drop(carId); this._carPassengers.delete(carId); this._applyPassengers(); },
      onPassengers: (carId, ids) => {
        if (ids.length) this._carPassengers.set(carId, ids);
        else this._carPassengers.delete(carId);
        this._applyPassengers();
      },
      // (Re)join: the crews that were already aboard before we got here.
      onPassengersSeed: (map) => {
        this._carPassengers.clear();
        for (const [carId, ids] of Object.entries(map ?? {})) {
          if (ids?.length) this._carPassengers.set(carId, ids);
        }
        this._applyPassengers();
      },
    });

    this.events.once('shutdown', () => {
      for (const c of this._parkedCars.values()) c.destroy();
      this._parkedCars.clear();
      for (const b of this._peerBikes.values()) b.destroy();
      this._peerBikes.clear();
      this._carPassengers.clear();
      this._riders.clear();
    });
  }

  // Everything that changes when the server says who is sitting in which car — the one
  // source of truth for it, since a passenger's own `move` comes back downgraded to a
  // walk (only a driver may claim a car) and riders would otherwise jog alongside at
  // 90 km/h. Re-run it whenever the roster, a join or a `car_passengers` lands: any of
  // the three can be the message that first pairs a rider with an avatar.
  _applyPassengers() {
    this._riders = new Set([...this._carPassengers.values()].flat());
    for (const [id, avatar] of this._presence.avatars) {
      if (avatar.isNpc) continue;
      avatar.setHidden(this._riders.has(id));
      avatar.setPassengerCount?.(this._carPassengers.get(avatar.carId)?.length ?? 0);
    }
    // Our own dashboard badge — the driver's copy of the same head-count.
    if (this.carCtl.isDriving) {
      const ids = this._carPassengers.get(this.carCtl.car.id) ?? [];
      const riders = ids.map((id) => this._presence.avatars.get(id));
      this.carCtl.setPassengers(ids.length, riders.map((a) => a?.character ?? 'natalka'), riders.map((a) => a?.look ?? null));
    }
    // …and are WE in a seat? Boarding, leaving, being kicked out, the driver parking and
    // the driver's socket dying all arrive as exactly this message.
    let seat = null;
    for (const [carId, ids] of this._carPassengers) {
      if (ids.includes(window.__myId)) { seat = carId; break; }
    }
    if (seat === this._ridingIn) return;
    if (seat) this._startRide(seat); else this._endRide();
  }

  // The roster and every `join` can carry a peer we already know is riding — re-apply so
  // an avatar that appears AFTER its `car_passengers` is hidden too, not left jogging.
  _attachPassengers() {
    const offs = [
      realtime.on('roster', (m) => { if (m.room === 'town') this._applyPassengers(); }),
      realtime.on('join', (m) => { if (m.room === 'town') this._applyPassengers(); }),
    ];
    // SPACE last, so the car controller's handler has already had its say: if it just put
    // us behind our own wheel, `isDriving` is true here and we don't also take a seat.
    this._rideSpaceHandler = () => this._onRideSpace();
    this.input.keyboard.on('keydown-SPACE', this._rideSpaceHandler);
    this.events.once('shutdown', () => {
      offs.forEach(off => off());
      this.input.keyboard.off('keydown-SPACE', this._rideSpaceHandler);
      this._clearRideAction();
      // Get out before we walk indoors. The server frees the seat on `unsubscribe`
      // anyway; saying so explicitly means the rest of the street hears the seat is
      // free straight away rather than whenever the room change lands.
      if (this._ridingIn) realtime.carLeave();
      this._ridingIn = null;
    });
  }

  // The avatar of the driver whose car we're in (they may not have spawned yet).
  _rideDriver() {
    if (!this._ridingIn) return null;
    for (const a of this._presence.avatars.values()) {
      if (a.carId === this._ridingIn && a.mode === 'car') return a;
    }
    return null;
  }

  // Every peer currently driving, as plain data for `boardableCar`. School cars are excluded:
  // the server always refuses `car_board school:…` (only the child on the lesson may be in
  // it), so offering a boarding prompt on one could only ever fail.
  _peerCars() {
    const out = [];
    for (const a of this._presence.avatars.values()) {
      if (a.isNpc || a.mode !== 'car' || !a.carId || isSchoolCarId(a.carId)) continue;
      out.push({ carId: a.carId, name: a.name, x: a.x, y: a.y, speed: a.speed, mode: 'car' });
    }
    return out;
  }

  // --- riding along ---------------------------------------------------------------------

  _startRide(carId) {
    this._ridingIn = carId;
    this._rideAt = null;
    this._rideBroadcastAt = 0;
    // Off your feet: the walker stops reading keys, the character stops being drawn (the
    // car is what's on screen now) and the camera moves over to the driver's car.
    this.walker.enabled = false;
    this.natalka.setVisible(false);
    const driver = this._rideDriver();
    this._followWithLag(driver?.car ?? driver?.sprite ?? this.natalka);
    showToast(this, 'Jedziesz z kimś {car} Wysiądź, kiedy auto stanie');
  }

  _endRide() {
    const carId = this._ridingIn;
    this._ridingIn = null;
    if (!carId) return;
    // Wherever the car was when the seat disappeared under us — we left, we were thrown
    // out, the driver parked, or the driver's socket died and the server tipped us out.
    const at = this._rideAt ?? { x: this.natalka.x, y: this.natalka.y, facing: 'right' };
    this._rideAt = null;
    this._leftCarId = carId;
    this._leftAt = Date.now();
    this._placeAfterRide(at.x, at.facing);
    this.walker.enabled = true;
    this.natalka.setVisible(true);
    this._followWithLag(this.natalka);
    // Don't walk straight into the shop we happened to stop in front of.
    this._spawnGuardUntil = Date.now() + 600;
  }

  // Stand the character beside a car that stopped at (x, y) facing `facing`, and tell both
  // the room and the save file about it — the walker has been off, so nothing else will.
  _placeAfterRide(x, facing) {
    const spot = exitPosition(x, facing, WORLD_WIDTH);
    this.natalka.setPosition(spot.x, spot.y);
    realtime.move({ x: spot.x, y: spot.y, facing, mode: 'walk' });
    if (!this.carCtl.isBusy) reportLocation(this, spot.x, spot.y, { immediate: true });
  }

  // `car_passengers` (empty) reaches us BEFORE the `vehicle_parked` that says where the
  // car actually came to rest, and the two can differ by a few frames of interpolation.
  // When the parked record for the car we just got out of turns up, use its real position.
  _settleAfterRide(car) {
    if (!car || car.car_id !== this._leftCarId || Date.now() - this._leftAt > 2000) return;
    this._leftCarId = null;
    const facing = car.facing === 'left' ? 'left' : 'right';
    this._placeAfterRide(car.x, facing);
  }

  // The frame of a passenger: no input, just the car's position copied onto us so the
  // camera, the room and the save file all agree on where we are.
  _rideFrame() {
    const driver = this._rideDriver();
    if (driver) {
      const target = driver.car ?? driver.sprite;
      if (this._camTarget !== target) this._followWithLag(target);
      this._rideAt = { x: driver.x, y: driver.y, facing: driver.facing };
    }
    const at = this._rideAt;
    if (!at) return;
    this.natalka.setPosition(at.x, at.y);
    if (!this.carCtl.isBusy) reportLocation(this, at.x, at.y);
    const now = performance.now();
    if (now - this._rideBroadcastAt >= RIDE_BROADCAST_MS) {
      this._rideBroadcastAt = now;
      // The server downgrades this to a walk anyway; what matters is the position, so a
      // peer joining mid-drive doesn't put us back on the kerb we boarded from.
      realtime.move({ x: at.x, y: at.y, facing: at.facing, mode: 'walk' });
    }
  }

  // The shared action button, for boarding and getting out. Runs AFTER the car's and the
  // bike's own sync, so when a seat is on offer it is the one thing the button does.
  _syncRideAction() {
    if (isInputLocked(this)) { this._clearRideAction(); return; }
    if (this._ridingIn) {
      // Only while the car is standing still: the server answers CAR_MOVING otherwise,
      // and a button that always fails is worse than no button.
      if ((this._rideDriver()?.speed ?? 0) === 0) {
        this._registerRideAction(LABEL_RIDE_EXIT, () => realtime.carLeave());
      } else {
        this._clearRideAction();
      }
      return;
    }
    if (this.carCtl.isDriving || this.bike.isRiding) { this._clearRideAction(); return; }
    const car = boardableCar(this._peerCars(), this.natalka);
    if (car) this._registerRideAction(rideBoardLabel(car.name), () => realtime.carBoard(car.carId));
    else this._clearRideAction();
  }

  _registerRideAction(label, fn) {
    this._rideActionLabel = label;
    virtualInput.registerAction(label, fn);
  }

  // Only ever take back a label we put there ourselves (the garden's „zbierz" and the
  // bike's „rower" share this button).
  _clearRideAction() {
    const a = virtualInput.getAction();
    if (a && a.label === this._rideActionLabel) virtualInput.clearAction();
    this._rideActionLabel = null;
  }

  _onRideSpace() {
    if (isInputLocked(this)) return;
    if (this._ridingIn) {
      if ((this._rideDriver()?.speed ?? 0) === 0) realtime.carLeave();
      return;
    }
    if (this.carCtl.isDriving || this.bike.isRiding) return;
    const car = boardableCar(this._peerCars(), this.natalka);
    if (car) realtime.carBoard(car.carId);
  }

  // Camera follows `target` horizontally with a soft lag (see CAMERA_TAU above).
  // The first call snaps so a scene never starts by panning in from the wrong place.
  _followWithLag(target) {
    const cam = this.cameras.main;
    cam.stopFollow();
    // Sub-pixel scrolling: with roundPixels the scroll snaps to whole pixels every frame,
    // which at walking speed alternates 1px/2px steps and reads as stutter.
    cam.roundPixels = false;
    const snap = !this._camTarget;   // first follow after create(): jump, don't pan in
    this._camTarget = target;
    if (snap) cam.scrollX = this._desiredScrollX();
  }

  // Centred on the target at rest; while driving the camera slides ahead of the car with
  // speed (see cameraLookahead.mjs) so the road ahead, not behind, fills the screen.
  _desiredScrollX() {
    const ahead = this.carCtl?.isDriving ? lookahead(this.carCtl.drive) : 0;
    const x = (this._camTarget?.x ?? 0) + ahead - GAME_WIDTH / 2;
    return Math.max(0, Math.min(WORLD_WIDTH - GAME_WIDTH, x));
  }

  _updateCamera(dt) {
    if (!this._camTarget) return;
    const cam = this.cameras.main;
    const k = 1 - Math.exp(-(dt / 1000) / (this._camTau || CAMERA_TAU));   // frame-rate independent easing
    cam.scrollX += (this._desiredScrollX() - cam.scrollX) * k;
  }

  // A new-version reload (ui/autoUpdate.js) waits while she drives or rides along: a car on the road is not saved.
  reloadBlocked() {
    return !!(this.carCtl?.isDriving || this._ridingIn);
  }

  update(_t, dt) {
    this._streetSky?.update();
    this._streetLook?.update(currentPhase(), dt);
    const driving = this.carCtl.isDriving;
    const riding = !!this._ridingIn;
    if (driving) this.carCtl.update(dt);
    else if (riding) this._rideFrame();
    else if (this.bike.isRiding) this.bike.update(dt);
    else this.walker.update(dt);

    // The faster you go the less lag the camera keeps, or a quick car sits on the screen edge.
    this._camTau = driving ? this._carCamTau() : CAMERA_TAU;
    this._updateCamera(dt);

    // The character herself is drawn in the same perspective as everything else: on the
    // pavement that is a 0–2% stretch, but it is the SAME code path that grows the child on
    // the training route's zebra, so it runs here too rather than being a special case.
    if (!driving && !this.bike.isRiding) {
      this.natalka.setWorldScale(WORLD_SCALE * perspectiveScale(this._feetY()));
    }

    // Whatever is carrying the player is the sprite that gets y-sorted, reported to the
    // location tracker, and tested against the doors and the right-edge exit.
    const mover = driving ? this.carCtl.sprite : (this.bike.isRiding ? this.bike.rider : this.natalka);
    // …and the people are sorted by their FEET (120 px of child, not 240), because the cars
    // are sorted by their wheels: everything on the road is in front of the pavement.
    depthSortScene(this, {
      actor: mover, presence: this._presence,
      halfH: CHAR_HALF_H, actorHalfH: this._moverHalfH(mover, driving),
    });
    // The car owns the shared action button whenever it has one to offer — and a seat in
    // someone else's car outranks both, so it gets the last word.
    if (!this.carCtl.syncAction()) this.bike?.syncBikeAction();
    this._syncRideAction();

    if (this._exitTriggered) return;
    // A passenger steers nothing: no doors, no edge exit, and no walking report — the
    // ride frame has already told the server where the car put us.
    if (riding) return;

    // Not while driving: the resume point should be where you got out on foot, not the
    // middle of the road — and set-location writes the whole state, so one landing on top
    // of an in-flight cars/park would revert the car to where it started.
    if (!driving && !this.carCtl.isBusy) reportLocation(this, mover.x, mover.y);

    // The lesson's way out of the street: the left edge, which for every other driver is
    // just where the road stops. Task 6's TrainingRouteScene takes it from here.
    if (driving && this.carCtl.isSchoolCar && this._lesson && mover.x <= LESSON_EDGE_X) {
      this._exitTriggered = true;
      this.scene.start(SCENE.TRAINING_ROUTE, { ...this._lesson });
      return;
    }

    // Right-edge exit — on foot or on the bike it leads to the front of the house; in your
    // own car it IS the drive home: the car goes into the garage and you step out in the
    // backyard. The school's car has no garage here, so it stops at the edge.
    if (mover.x >= WORLD_WIDTH - 80) {
      if (driving && this.carCtl.isSchoolCar) {
        this._notifyCarBlock(this._carBlockText('Autem szkoły nie da się wjechać do domu {car}'));
        this.carCtl.stopAt(WORLD_WIDTH - 90);
        return;
      }
      if (driving) {
        this._exitTriggered = true;
        this.carCtl.stopAt(WORLD_WIDTH - 90);
        this.carCtl.parkGarage({ fromEdge: true }).then((parked) => {
          if (parked) this.scene.start(SCENE.BACKYARD);
          else this._exitTriggered = false;
        });
        return;
      }
      this._exitTriggered = true;
      this.scene.start(SCENE.MAIN, { spawnX: 250 });
      return;
    }

    // Spawn cooldown — pomija trigger drzwi w pierwszych 600ms po wejściu do sceny
    if (Date.now() < this._spawnGuardUntil) return;

    // Door check — where her FEET are, so a shorter character opens the same doors from the
    // same square of pavement.
    const feetY = mover.y + this._moverHalfH(mover, driving);
    for (const dz of this.doorZones) {
      if (Phaser.Geom.Rectangle.Contains(dz.rect, mover.x, feetY)) {
        if (driving) {
          this._notifyCarBlock(this._carBlockText('Zaparkuj auto, żeby wejść {car}'));
          return;
        }
        if (this.bike.isRiding) { this._notifyBikeBlock(); return; }
        if (dz.comingSoon) { this._notifyComingSoon(dz.comingSoon); return; }
        if (doorClosed(dz.key, currentPhase())) { this._notifyClosed(); return; }
        this._exitTriggered = true;
        this.scene.start(dz.target);
        return;
      }
    }
  }

  // How far below the mover's centre it touches the ground, so `y + this` is its ground
  // line: a car's wheels, a bike's wheels, or the character's feet. All three grow with the
  // perspective, so all three are measured off the sprite that is actually on screen.
  _moverHalfH(mover, driving) {
    if (driving) return this.carCtl.sprite?.wheelOffset ?? CHAR_HALF_H;
    if (this.bike.isRiding) return this.bike.riderWheelOffset;
    return this._feetY() - this.natalka.y;
  }

  /** Where the walking character's feet are — her sprite centre run through the perspective. */
  _feetY() { return groundYFromCentre(CHAR_HALF_H, this.natalka.y); }

  // Camera lag while driving: CAMERA_TAU when crawling, CAMERA_TAU_FAST flat out.
  _carCamTau() {
    const t = Math.min(1, pxPerSec(this.carCtl.drive?.v ?? 0) / CAMERA_TAU_FAST_AT);
    return CAMERA_TAU + (CAMERA_TAU_FAST - CAMERA_TAU) * t;
  }

  // Why you can't leave the car here. Mid-lesson the answer is never "park it" (you can't)
  // and never "drop your passengers" (the instructor is staying) — it's "we're going left".
  _carBlockText(parkText) {
    if (this.carCtl.isSchoolCar) return LESSON_BUBBLE_PL;
    return this.carCtl.passengerCount > 0 ? 'Najpierw wysadź pasażerów {friends}' : parkText;
  }

  // Can't get out of the car through a shop door / the edge of the street (throttled).
  _notifyCarBlock(text) {
    const now = Date.now();
    if (now - (this._carBlockToastAt ?? 0) < 2000) return;
    this._carBlockToastAt = now;
    showToast(this, text);
  }

  // Can't enter a shop while on the bike — nudge the player to get off (throttled).
  _notifyBikeBlock() {
    const now = Date.now();
    if (now - this._bikeBlockToastAt < 2000) return;
    this._bikeBlockToastAt = now;
    showToast(this, 'Zejdź z roweru, żeby wejść do sklepu {bike}');
  }

  // A shop closed for the night (street/streetLooks.mjs CLOSED_AT_NIGHT): say so instead of entering (throttled).
  _notifyClosed() {
    const now = Date.now();
    if (now - this._closedToastAt < 2000) return;
    this._closedToastAt = now;
    showToast(this, CLOSED_TOAST);
  }

  // Door for a building that isn't built yet — say so instead of entering (throttled).
  _notifyComingSoon(text) {
    const now = Date.now();
    if (now - (this._comingSoonToastAt ?? 0) < 2000) return;
    this._comingSoonToastAt = now;
    showToast(this, text);
  }
}
